"use client";

import { format, isPast, isThisWeek } from "date-fns";
import { type SanityReference } from "next-sanity";
import React, { useEffect, useRef } from "react";
import Ad from "src/components/ads/Ad";
import LoadingIndicator from "src/components/loadingindicator/LoadingIndicator";
import NoLocation from "src/components/program/NoLocation";
import type { PosterGridAdConfig } from "src/components/program/PosterGridAdConfig";
import ProgramControls from "src/components/program/ProgramControls";
import ResetFilter from "src/components/program/ResetFilter";
import filterMoviesAndShows, { type FilteredMovies } from "src/components/program/filterMoviesAndShows";
import { useFilminfo } from "src/lib/client/useFilminfo";
import useSsrSafeMediaQuery from "src/lib/client/useSsrSafeMediaQuery";
import withScrollRestoration, { type SupportsScrollRestoration } from "src/lib/client/withScrollRestoration";
import { useLocationContext, useProgramDatesContext, useProgramFilterContext, useSelectedCinemaContext } from "src/lib/contexts/ContextTypes";
import { locale } from "src/lib/locale/date-fns";
import { type Movie, type Show } from "src/lib/movieinfo/movieinfotypes";
import { PROGRAM_FOR_DATE_QUERY, SHOWDATES_QUERY } from "src/lib/movieinfo/programQueries";
import { type ILoaderMoviesProps } from 'src/lib/types/filminfo/ILoaderMoviesProps';
import { type ProgramMovie } from "src/lib/types/filminfo/ProgramMovie";
import styles from './KinoprogramLoader.module.css';

//#region [Props]
type KinoprogramLoaderProps = {
  children: React.ReactNode;
  hasSponsor: boolean;
  sponsorClickUrl?: string | null;
  sponsorTopImageDesktop?: SanityReference[] | null;
  sponsorTopImageMobile?: SanityReference[] | null;
  hasSponsorBg?: boolean;
  predefinedLocation?: string;
  predefinedCinema?: string;
  disableLocationSelector?: boolean;
  disableFilter?: boolean;
  adConfig?: PosterGridAdConfig;
  disableDateSelector?: boolean;
} & SupportsScrollRestoration;
//#endregion

//#region [Component]
function KinoprogramLoader({
  children,
  isReadyForScrollRestoration,
  hasSponsor,
  sponsorClickUrl,
  sponsorTopImageDesktop,
  sponsorTopImageMobile,
  hasSponsorBg,
  predefinedLocation,
  predefinedCinema,
  disableLocationSelector,
  disableFilter,
  adConfig,
  disableDateSelector
}: KinoprogramLoaderProps) {
  const isDesktop = useSsrSafeMediaQuery("(min-width: 550px)");
  const locationContext = useLocationContext();
  const selectedDateContext = useProgramDatesContext();
  const filterContext = useProgramFilterContext();
  const cinemaContext = useSelectedCinemaContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    fiLoading: isLocationDataLoading,
    fiData: locData
  } = useFilminfo(SHOWDATES_QUERY, {
    locations: locationContext.location,
    excludeMovieIdPrefixes: ["KUL"],
    firmId: locationContext.firmId
  }, {
    active: (locationContext.location ?? []).length > 0 || !!locationContext.firmId,
    staleTime: 10 * 60 * 1000
  });
  const {
    fiLoading: _isShowsLoading,
    fiData: _showData
  } = useFilminfo(PROGRAM_FOR_DATE_QUERY, {
    locations: locationContext.location,
    date: selectedDateContext.selectedDate && !selectedDateContext.showingAllDates ? format(selectedDateContext.selectedDate, 'yyyy-MM-dd') : null,
    firmId: locationContext.firmId
  }, {
    active: (!!selectedDateContext.selectedDate || selectedDateContext.showingAllDates) && ((locationContext.location ?? []).length > 0 || !!locationContext.firmId),
    staleTime: 10 * 60 * 1000
  });
  useEffect(() => {
    if (!isLocationDataLoading) {
      const dates = locData?.showQuery?.getShowDates?.map(date => new Date(date!.date!));
      if ((dates?.length ?? 0) > 0) {
        selectedDateContext.setAvailableDates(dates as Date[]);
      } else {
        selectedDateContext.setAvailableDates(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locData, isLocationDataLoading]);
  useEffect(() => {
    if (!_isShowsLoading) {
      isReadyForScrollRestoration?.(true);
    }
  }, [_isShowsLoading, isReadyForScrollRestoration]);
  let filteredMovies: FilteredMovies | null = null;
  if (!_isShowsLoading && (_showData?.movieQuery?.getCurrentMovies?.length ?? 0) > 0) {
    const _filteredMovies = _showData!.movieQuery!.getCurrentMovies!.filter(movie => {
      if (movie?.mainVersionId?.startsWith("KUL")) {
        return false;
      }
      if (movie?.shows?.every(show => isPast(new Date(show?.showStart ?? 0)))) {
        return false;
      }
      return true;
    }) as Movie[];
    const _transformedMovies = _filteredMovies.map(movie => {
      const s = movie.shows?.map(show => ({
        ...(show as Show),
        dateShowStart: new Date(show?.showStart ?? 0),
        perspectives: ensureAtLeast2DIsPresent(show!)
      }));
      const tMovie: ProgramMovie = {
        ...movie,
        isPremieringThisWeek: isPremieringThisWeek(movie.premiere!),
        showsWithDate: s!,
        filteredShows: s!
      };
      return tMovie;
    });
    filteredMovies = filterMoviesAndShows(_transformedMovies, filterContext, cinemaContext.selectedCinemas, predefinedCinema);
  }
  const atLeastOneLocationSelected = (locationContext.location?.length ?? 0) > 0;
  return <div className={styles.SProgramContainer} ref={containerRef}>
        <ProgramControls hasSponsor={hasSponsor} sponsorClickUrl={sponsorClickUrl} sponsorTopImageDesktop={sponsorTopImageDesktop} sponsorTopImageMobile={sponsorTopImageMobile} hasSponsorBg={hasSponsorBg} scrollToRef={containerRef} predefinedLocation={predefinedLocation} predefinedCinema={predefinedCinema} disableLocationSelector={disableLocationSelector} disableFilter={disableFilter} disableDateSelector={disableDateSelector} />
        {(isLocationDataLoading || _isShowsLoading) && <LoadingIndicator />}
        {(atLeastOneLocationSelected || disableLocationSelector) && !(isLocationDataLoading || _isShowsLoading) && <>
            {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            movies: filteredMovies?.movies,
            numFilteredMovies: filteredMovies?.numFilteredMovies,
            numFilteredShows: filteredMovies?.numFilteredShows
          } as ILoaderMoviesProps);
        }
        return child;
      })}

            {((filteredMovies?.numFilteredMovies ?? 0) > 0 || (filteredMovies?.numFilteredShows ?? 0) > 0) && <ResetFilter ref={containerRef} numFilteredMovies={filteredMovies?.numFilteredMovies} numFilteredShows={filteredMovies?.numFilteredShows} className={styles.SFilterInfo} />}
        </>}
        {!disableLocationSelector && !isLocationDataLoading && !atLeastOneLocationSelected && <>
            <NoLocation />
            {(isDesktop ? adConfig?.desktop : adConfig?.mobile)?.filter(p => p.showWhenEmpty).map(placement => <Ad key={placement.targetId} className={styles.NoLocationAd} targetId={placement.targetId} removeIfNoAd={placement.removeIfNoAd} supportsFixedAd={placement.supportsFixedAd} />)}
        </>}
    </div>;
}
//#endregion

export default withScrollRestoration(KinoprogramLoader, 500);

//#region [Other] Helpers
function isPremieringThisWeek(premiere: string) {
  if (!premiere || premiere === "0001-01-01T00:00:00") {
    return false;
  }
  return isThisWeek(new Date(premiere), {
    locale,
    weekStartsOn: 1
  });
}

/**
 * Make sure every show has the correct dimensions. A show with no dimension tags is 2D. These dimensions tags will be stored in show.dims
 * @param {Show} show the show to mark
 */
function ensureAtLeast2DIsPresent(show: Show): string[] {
  let perspectives = show.versionTags?.filter(t => t!.tag === "3D" || t!.tag === "2D").map(t => t!.tag);
  if ((perspectives?.length ?? 0) === 0) {
    perspectives = ["2D"];
  }
  return perspectives as string[];
}
//#endregion